import React__default from 'react';
import cn from 'classnames';
import { sanitizeRowProps } from '../util.js';
import { FocusScope } from '@react-aria/focus';

const renderCell = (cell, row) => {
  const props = { ...cell.getCellProps(),
    className: cn('yt-table__cell flex-1 truncate p-2 align-middle', cell.column.className, {
      'justify-start text-left': cell.column.align === 'left',
      'justify-end text-right': cell.column.align === 'right',
      'text-center': !cell.column.align
    }),
    style: { ...cell.column.style,
      flex: typeof cell.column.flex === 'function' ? cell.column.flex(row) : cell.column.flex
    }
  };
  return React__default.createElement("div", Object.assign({}, props, {
    role: "gridcell",
    "data-taco": "table-cell"
  }), cell.render('Cell') || null);
};

const Row = /*#__PURE__*/React__default.forwardRef(function TableRow({
  row,
  index,
  instance,
  headerGroups,
  ...rowProps
}, ref) {
  const {
    activeIndex,
    onRowClick,
    rowClassName,
    rowExpansionRenderer,
    rowHeight,
    setActiveIndex,
    style,
    inlineEditingUniqueId
  } = rowProps;
  const sanitizedRow = sanitizeRowProps(row, rowExpansionRenderer);
  const props = { ...row.getRowProps(),
    style: { ...style,
      minHeight: rowHeight ? `${rowHeight}px` : undefined,
      paddingLeft: row.depth ? `${row.depth * 2}rem` : undefined
    },
    className: cn('yt-table__row border-grey-light flex border-b min-h-[2.5rem] hover:bg-grey-light', {
      'yt-table__row--active bg-grey-light hover:bg-grey-light': activeIndex === index,
      'yt-table__row--clickable': !!onRowClick,
      'yt-table__row--dragging': !!row.isDragging,
      'yt-table__row--editing': !!row.isEditing
    }, typeof rowClassName === 'function' ? rowClassName(sanitizedRow) : rowClassName),
    onClick: onRowClick ? event => {
      if (event.target === event.currentTarget || event.target.classList.contains('yt-table__cell')) {
        event.preventDefault();
        event.persist();
        setActiveIndex(index);
        onRowClick(sanitizedRow);
      }
    } : () => {
      setActiveIndex(index);
    }
  };
  let cells = row.cells;

  if (row.depth > 0) {
    cells = row.cells.filter(cell => !cell.column.hiddenOnSubRows);
  }

  const expandedRow = rowExpansionRenderer && row.isExpanded && React__default.createElement("div", null, rowExpansionRenderer(sanitizedRow, instance));
  const rowContent = React__default.useMemo(() => {
    return React__default.createElement(React__default.Fragment, null, React__default.createElement(FocusScope, {
      key: row.original._createKey,
      contain: true,
      restoreFocus: true,
      autoFocus: true
    }, React__default.createElement("div", Object.assign({}, props, {
      role: "row",
      ref: ref
    }), React__default.createElement("input", {
      type: "hidden",
      name: "_rowIndexPath",
      value: row.id
    }), React__default.createElement("input", {
      type: "hidden",
      name: "_inlineEditingUniqueId",
      value: row.original[inlineEditingUniqueId]
    }), cells.map(cell => renderCell(cell, sanitizedRow)))), expandedRow);
  }, [row.original._createKey]);

  if (row.isEditing) {
    return rowContent;
  }

  return React__default.createElement(React__default.Fragment, null, React__default.createElement("div", Object.assign({}, props, {
    role: "row",
    ref: ref
  }), cells.map(cell => renderCell(cell, sanitizedRow))), expandedRow);
});

export { Row };
//# sourceMappingURL=renderRow.js.map
